/**
 * ceremony_props.ts — generated 3D props for the living-events stages
 * (the Hugging Face ceremony wave, 2026-09-10).
 *
 * "For 3D objects associated to a ceremony use Hugging Face to generate 3D
 * models to replace some of the procedurally generated ones." The props were
 * made by `tools/asset-pipeline/gen-ceremony-hf.py` (FLUX.1-schnell image →
 * Hunyuan3D-2.1 textured mesh, both on Hugging Face) and cooked by
 * `cook-ceremony-props.mjs` into `client/public/props/ceremony/<key>.glb`
 * (+ `manifest.json`), a STATIC side-channel like the audio and picker
 * thumbs — no registry row, no MinIO, so the stages work on any static host
 * (the migrate folder copies every public folder).
 *
 * The pure part here is the FIT: every prop is seated by its bounding box
 * bottom-centre (the PIVOT law) and uniformly scaled to the height the stage
 * wants; the loader keeps one master per key and hands out clones. A key
 * without a cooked file keeps its procedural fallback — the stages never
 * wait on the network.
 */

/** Where the cooked props live (relative to the page's base — dev, /atlas/ overlay, root overlay). */
export const CEREMONY_PROP_DIR = 'props/ceremony';

/** Prop keys and the height (m) each stage scales them to. */
export const CEREMONY_PROPS: Record<string, { targetH: number; label: string }> = {
  altar: { targetH: 0.95, label: 'shrine altar' },
  lectern: { targetH: 1.25, label: 'speaker lectern' },
  ballot_urn: { targetH: 0.8, label: 'voting urn' },
  feast_table: { targetH: 0.9, label: 'feast table' },
  fire_pit: { targetH: 0.5, label: 'fire pit' },
  lantern_post: { targetH: 3.6, label: 'lantern post' },
};
export type CeremonyPropKey = keyof typeof CEREMONY_PROPS;

export interface Box3Like { min: { x: number; y: number; z: number }; max: { x: number; y: number; z: number } }

/**
 * The transform that seats a model's bounding box bottom-centre on the origin
 * and scales it uniformly to `targetH` metres tall: apply `scale` to the
 * model, then translate by (dx, dy, dz) in the SCALED frame.
 */
export function fitTransform(box: Box3Like, targetH: number): { scale: number; dx: number; dy: number; dz: number } {
  const h = Math.max(1e-6, box.max.y - box.min.y);
  const scale = targetH / h;
  const cx = (box.min.x + box.max.x) / 2;
  const cz = (box.min.z + box.max.z) / 2;
  return { scale, dx: -cx * scale, dy: -box.min.y * scale, dz: -cz * scale };
}

/** The manifest the cook writes beside the files. */
export interface CeremonyManifest {
  version: number;
  props: Record<string, { file: string; tris: number; bytes: number; sizeM: [number, number, number] }>;
}

/** The URL of a prop file for the current page base. */
export function ceremonyPropUrl(file: string, baseUri: string): string {
  return new URL(`${CEREMONY_PROP_DIR}/${file}`, baseUri).toString();
}
